import React from 'react';
import Styled from 'styled';

import { Query, Mutation } from '@apollo/client/react/components';
import { mapAfter } from 'apollo-relay/update-query';

import { SectionCountConsumer } from 'lib/section-count-context/section-count-context';
import LoadingProgress from 'lib/react-router/loading-progress';
import observe from 'lib/intersection-observer/observe';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import gaTrack from 'four-nets/lib/google-analytics/track';
import ErrorPage from 'four-nets/errors/error-page';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';
import { ViewerFragment } from 'four-nets/user/viewer/viewer.graphql';

import PlaceholderPost from '../posts/feed-post/placeholder-post';
import query, { FeedLoaderQuery, FeedLoaderQueryVariables } from './feed-loader.graphql';
import mutation, { SetFriendsLastVisitMutation } from './mutations/set-friends-last-visit.graphql';
import Feed from './feed';

import defaultStyles from './feed.scss';

export const FIRST = 5;
export const PAGE_SIZE = 25;
export const AUTO_LOAD_COUNT = PAGE_SIZE / FIRST;

interface Props {
    styles: typeof defaultStyles;
    inLikedPhotoBlog: boolean;
    isStaffpick: boolean;
    mySelection: boolean;
    professionals: boolean;
    pathname: string;
    myComments: boolean;
    myLikes: boolean;
    search?: string;
    hashtag?: string;
    selection?: string;
    mostFavorite?: boolean;
    offset?: number;
}

interface PropsWithViewer extends Props {
    viewer: ViewerFragment | null;
}

interface State {
    loading?: boolean;
    visible: boolean;
    autoLoadCount: number;
    infinite: boolean;
}

class FeedLoader extends React.PureComponent<PropsWithViewer, State> {
    fetchMore?: () => Promise<any> = undefined;
    refetch?: () => Promise<any>;
    unobserve?: () => void;
    ref: React.RefObject<HTMLDivElement>;

    constructor(props: PropsWithViewer) {
        super(props);
        this.ref = React.createRef();
        this.state = {
            visible: false,
            autoLoadCount: AUTO_LOAD_COUNT - 1,
            infinite: false,
        };
    }

    componentDidMount() {
        const element = this.ref && (this.ref.current as Element | null);
        if (element) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }
    }

    componentWillUnmount() {
        if (this.unobserve) {
            this.unobserve();
        }
    }

    initObserveElement = (force: boolean = false) => {
        const element = this.ref && (this.ref.current as Element | null);
        if (element && (!this.unobserve || force)) {
            if (this.unobserve) {
                this.unobserve();
                this.unobserve = undefined;
            }
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '350px' }
            );
            this.setState(prevState => ({
                autoLoadCount: AUTO_LOAD_COUNT,
                visible: false,
                infinite: prevState.infinite || force,
            }));
        }
    };

    componentDidUpdate(prevProps: Props, prevState: State) {
        this.initObserveElement();

        const { infinite, autoLoadCount, loading, visible } = this.state;

        /* If we already loaded the page for X times, we unobserve the fetchMore button  */
        let autoLoadActive = true;
        if (!infinite && prevState.autoLoadCount > 0 && autoLoadCount <= 0) {
            if (this.unobserve) {
                this.unobserve();
                this.unobserve = undefined;
            }
            autoLoadActive = false;
        }

        /* When the fetchMore button appeared, we want to fetchMore if autoload is still active */
        const appeared = visible && !prevState.visible;
        if (autoLoadActive && appeared && !loading && this.fetchMore) {
            this.fetchMore().then(() => {
                this.setState(prevState => ({ visible: false, autoLoadCount: prevState.autoLoadCount - 1 }));
            });
        }

        /* When user changed a category, we want to reset the autoLoad counter */
        const prevCategories = [
            prevProps.inLikedPhotoBlog,
            prevProps.mySelection,
            prevProps.myComments,
            prevProps.myLikes,
            prevProps.professionals,
            prevProps.mostFavorite,
        ];
        const curCategories = [
            this.props.inLikedPhotoBlog,
            this.props.mySelection,
            this.props.myComments,
            this.props.myLikes,
            this.props.professionals,
            this.props.mostFavorite,
        ];

        const categoryChange = JSON.stringify(prevCategories) !== JSON.stringify(curCategories);
        if (categoryChange) {
            this.setState({
                autoLoadCount: AUTO_LOAD_COUNT - 1,
                infinite: false,
            });
        }

        const offsetChange = prevProps.offset !== this.props.offset;
        if (offsetChange) {
            this.setState({ autoLoadCount: AUTO_LOAD_COUNT - 1 });
        }
    }

    render() {
        const {
            styles,
            inLikedPhotoBlog,
            mySelection,
            professionals,
            myComments,
            myLikes,
            search,
            hashtag,
            isStaffpick,
            selection,
            mostFavorite,
            offset,
            viewer,
        } = this.props;

        const { autoLoadCount } = this.state;

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${offset - 1}`) || undefined;
        }

        if (!viewer && (inLikedPhotoBlog || myComments || myLikes || mySelection)) {
            return <Unauthorized />;
        }

        return (
            <SectionCountConsumer>
                {({ sectionCounts }) => (
                    <Mutation<SetFriendsLastVisitMutation>
                        mutation={mutation}
                        refetchQueries={['FriendsLastVisitCount']}
                    >
                        {setFriendsLastVisit => (
                            <Query<FeedLoaderQuery, FeedLoaderQueryVariables>
                                query={query}
                                variables={
                                    {
                                        first: FIRST,
                                        after,
                                        inLikedPhotoBlog,
                                        search,
                                        hashtag,
                                        isStaffpick,
                                        mySelection,
                                        professionals,
                                        myComments,
                                        myLikes,
                                        mostFavorite: mostFavorite ? 3 : undefined,
                                        selectionItem: selection,
                                        orderBy: isStaffpick
                                            ? '-staff_picked_time'
                                            : mostFavorite
                                            ? '-likes'
                                            : undefined,
                                        isAuth: Boolean(viewer),
                                        queryKey: sectionCounts.SECTION_BLOGS,
                                    } as FeedLoaderQueryVariables
                                }
                                notifyOnNetworkStatusChange={true}
                                fetchPolicy='cache-first'
                                onCompleted={() => {
                                    if (inLikedPhotoBlog) {
                                        setFriendsLastVisit();
                                    }

                                    if (!after) {
                                        this.setState({ visible: false });
                                    }
                                }}
                            >
                                {({ error, data, loading, refetch, fetchMore }) => {
                                    const endCursor = data && data.feed && data.feed.pageInfo.endCursor;
                                    let nextOffset;

                                    const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
                                        endCursor,
                                        pathname: this.props.pathname,
                                        pageSize: FIRST,
                                    });

                                    if (endCursor) {
                                        nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
                                    } else {
                                        nextOffset = offset;
                                    }

                                    if (data && data.feed && data.feed.pageInfo && data.feed.pageInfo.hasNextPage) {
                                        this.fetchMore = () => {
                                            this.setState({ loading: true });

                                            return fetchMore({
                                                variables: { after: endCursor },
                                                updateQuery: (
                                                    previousResult = {} as FeedLoaderQuery,
                                                    { fetchMoreResult = {} as FeedLoaderQuery }
                                                ) => {
                                                    return {
                                                        ...previousResult,
                                                        ...fetchMoreResult,
                                                        feed: mapAfter(previousResult.feed, fetchMoreResult.feed),
                                                    };
                                                },
                                            }).finally(() => {
                                                this.setState({ loading: false });
                                            });
                                        };
                                    } else {
                                        this.fetchMore = undefined;
                                    }

                                    return (
                                        <ErrorPage error={error}>
                                            <LoadingProgress loading={loading} block={false}>
                                                <Feed
                                                    loading={loading}
                                                    data={data}
                                                    refetch={refetch}
                                                    mySelection={mySelection}
                                                    showFeedAds={true}
                                                />
                                                {this.state.loading ? (
                                                    <div className={styles.wrapper}>
                                                        <div className={styles.container}>
                                                            {[null, null, null, null].map((_, index) => (
                                                                <PlaceholderPost key={index} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : null}
                                                <div ref={this.ref as any} className={styles.fetchMoreWrapper}>
                                                    {this.fetchMore && !loading ? (
                                                        <FetchMoreButton
                                                            style={{ button: styles.fetch }}
                                                            disabled={!this.fetchMore}
                                                            href={endCursorHref}
                                                            prevHref={prevCursorHref}
                                                            onClick={() => {
                                                                if (this.fetchMore) {
                                                                    this.fetchMore().then(() => {
                                                                        this.setState(prevState => ({
                                                                            visible: false,
                                                                            autoLoadCount: prevState.autoLoadCount - 1,
                                                                        }));
                                                                    });

                                                                    if (typeof window !== 'undefined') {
                                                                        gaTrack('pageview', endCursorHref);
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    ) : null}
                                                    {(data &&
                                                        data.feed &&
                                                        data.feed.edges!.length !== 0 &&
                                                        data.feed.edges!.length % PAGE_SIZE === 0) ||
                                                    autoLoadCount <= 0 ? (
                                                        <OffsetPager
                                                            style={{ nav: styles.nav }}
                                                            pageSize={PAGE_SIZE}
                                                            offset={nextOffset}
                                                        />
                                                    ) : null}
                                                </div>
                                            </LoadingProgress>
                                        </ErrorPage>
                                    );
                                }}
                            </Query>
                        )}
                    </Mutation>
                )}
            </SectionCountConsumer>
        );
    }
}

export default Styled(defaultStyles)(FeedLoader);
